export const calendarColumnAttributes = {
  dataModelFieldName: 'date',
  type: 'calendar',
  width: 100,
  align: 'left',
  options: {
    format: 'dd.mm.yyyy',
    placeholder: 'dd.mm.yyyy',
    readonly: false,
    today: false,
    startingDay: 1,
    textDone: 'OK',
    validRange: ['1500-01-01', '9999-12-31'],
    // other calendar options are here: https://bossanova.uk/jspreadsheet/v3/docs/getting-started
  },
};

const dottedDateRegex = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/;
const isoDateRegex = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?(?:Z)?$/;

function isValidDateParts(year, month, day) {
  const parsedDate = new Date(Date.UTC(year, month - 1, day));
  return parsedDate.getUTCFullYear() === year && parsedDate.getUTCMonth() === month - 1 && parsedDate.getUTCDate() === day;
}

export function normalizeCalendarValue(value, format = 'dd.mm.yyyy') {
  if (typeof value !== 'string') return value;

  const trimmedValue = value.trim();
  if (!trimmedValue) return value;

  const isoMatch = trimmedValue.match(isoDateRegex);
  if (isoMatch) {
    const [, year, month, day, hour = '00', minute = '00', second = '00'] = isoMatch;
    if (isValidDateParts(Number(year), Number(month), Number(day))) {
      return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
    }
    return value;
  }

  if (format?.toLowerCase() !== 'dd.mm.yyyy') return value;

  const dottedDateMatch = trimmedValue.match(dottedDateRegex);
  if (!dottedDateMatch) return value;

  const [, day, month, year] = dottedDateMatch;
  if (!isValidDateParts(Number(year), Number(month), Number(day))) return value;

  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')} 00:00:00`;
}

export function getTodayCalendarValue(today = new Date()) {
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day} 00:00:00`;
}

export function isCalendarValueParseable(value, format = 'dd.mm.yyyy') {
  if (typeof value !== 'string') return false;

  const trimmedValue = value.trim();
  if (!trimmedValue) return false;

  const normalizedValue = normalizeCalendarValue(trimmedValue, format);
  const isoMatch = normalizedValue.match(isoDateRegex);
  if (!isoMatch) return false;

  const [, year, month, day] = isoMatch;
  return isValidDateParts(Number(year), Number(month), Number(day));
}

export function getCalendarEditorValue(value, format = 'dd.mm.yyyy', fallbackDate = new Date()) {
  if (isCalendarValueParseable(value, format)) {
    return normalizeCalendarValue(value, format);
  }

  return getTodayCalendarValue(fallbackDate);
}

export function formatCalendarDisplayValue(value, format = 'dd.mm.yyyy') {
  const normalizedValue = normalizeCalendarValue(value, format);
  const isoMatch = typeof normalizedValue === 'string' ? normalizedValue.match(isoDateRegex) : null;
  if (!isoMatch) return value;

  const [, year, month, day] = isoMatch;
  if (format?.toLowerCase() === 'dd.mm.yyyy') {
    return `${day}.${month}.${year}`;
  }

  return `${year}-${month}-${day}`;
}

export function handleDate(date) {
  if (!date) return date;
  // if date contains space, assume this is the following string '2015-04-01 00:00:00'
  const localDate = date.indexOf(' ') > -1 ? `${date.replace(' ', 'T')}Z` : date;
  // if date was picked from the calendar widget, it will be in the ISO8601 format
  if (localDate.includes('Z')) return new Date(`${date.split('Z')[0]}Z`).setUTCHours(0, 0, 0, 0);
  // if user pasted the date, we assume it is in the same format as displayed in the table (DD.MM.YYYY)
  // user-pased date is interpreted as a CET date (midnight), so when we convert it to UTC, it will be the previous day 10 or 11 pm
  let pastedDate = new Date(date.split('.')[2], Number(date.split('.')[1]) - 1, date.split('.')[0]);
  pastedDate = pastedDate.setTime(pastedDate.getTime() - pastedDate.getTimezoneOffset() * 60 * 1000);
  return pastedDate;
}

const convertableTypes = [
  'currency',
  'currency0Decimals',
  'currency2Decimals',
  'currency4Decimals',
  'secondaryCurrency0Decimals',
  'secondaryCurrency2Decimals',
  'secondaryCurrency',
  'number',
  'number0Decimals',
  'number2Decimals',
  'number4Decimals',
];

// change date format, remove empty strings, convert amounts (strings, with mask) to numbers
// leave the remaining properties unchanged
// bases on gridLayout, so it requires the transaction object to have the exact same sequence of keys as specified the gridLayout
// if this becomes a problem, use Map instead of Object
export function removeGridFormatting(transactionArray, gridLayout) {
  // this regex removes all non-numerical characters from a string
  const amountRegex = /[^0-9.}-]+/g;

  return transactionArray.map((transaction) => {
    const newTransaction = { ...transaction };
    Object.keys(newTransaction).forEach((key, index) => {
      // replace '' with nulls
      if (String(newTransaction[key]).length === 0) newTransaction[key] = null; // numbers don't have .length, so String()
      // convert ISO dates to epoch
      if (gridLayout[index]?.type === 'date') newTransaction[key] = handleDate(newTransaction[key]);
      // convert amounts to numbers (for predetermined column types)
      // console.log('DEBUG removeGridFormatting', key, gridLayout[index]);
      if (convertableTypes.includes(gridLayout[index]?.type)) {
        // console.log('DEBUG removeGridFormatting', key, newTransaction[key]);
        newTransaction[key] = Number(String(newTransaction[key]).replace(amountRegex, ''));
      }
    });
    return newTransaction;
  });
}

export function compare(a, b, propertyName, asc = true) {
  // console.log('DEBUG compare', a, b, propertyName, asc);
  const multiplier = asc ? 1 : -1;
  // so that it doesn't sort capital letters separately from lowercase
  const elementA = typeof a[propertyName] === 'number' ? a[propertyName] : a[propertyName].toLowerCase();
  const elementB = typeof b[propertyName] === 'number' ? b[propertyName] : b[propertyName].toLowerCase();

  if (elementA < elementB) {
    return -1 * multiplier;
  }
  if (elementA > elementB) {
    return 1 * multiplier;
  }
  return 0;
}
